import { useEffect, useMemo, useState } from "react";
import { ImagePlus, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/utils/api";
import { useBrandLogos } from "@/contexts/BrandLogoContext";
import { BRAND_LOGO_OPTIONS, hasStaticBrandLogo, normalizeBrand } from "@/utils/format";
import StockBrandLogo from "@/components/StockBrandLogo";

export default function BrandLogoManager({ initialBrand = "" }) {
  const { logos, items, refreshLogos } = useBrandLogos();
  const [brand, setBrand] = useState(() => initialBrand.trim());
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const key = normalizeBrand(brand);
  const current = logos[key];

  const previewUrl = useMemo(() => file ? URL.createObjectURL(file) : "", [file]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  useEffect(() => {
    setBrand(initialBrand.trim());
  }, [initialBrand]);

  useEffect(() => {
    setScale(Number(current?.scale ?? 1));
    setOffsetX(Number(current?.offset_x ?? 0));
    setOffsetY(Number(current?.offset_y ?? 0));
    setFile(null);
  }, [key, current?.scale, current?.offset_x, current?.offset_y]);

  const presentation = { ...current, scale, offset_x: offsetX, offset_y: offsetY };

  const save = async (event) => {
    event.preventDefault();
    if (!key) return toast.error("Marka adı girin");
    if (!file && !current?.has_custom_image && !hasStaticBrandLogo(key)) {
      return toast.error("Yeni bir marka için logo dosyası seçin");
    }
    setSaving(true);
    try {
      const data = new FormData();
      data.append("display_name", brand.trim());
      data.append("scale", String(scale));
      data.append("offset_x", String(offsetX));
      data.append("offset_y", String(offsetY));
      if (file) data.append("file", file);
      await api.put(`/settings/brand-logos/config/${encodeURIComponent(key)}`, data);
      await refreshLogos();
      setFile(null);
      toast.success("Logo ayarı kaydedildi");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!current) return;
    if (!window.confirm(`${brand} için özel logo ve görünüm ayarları kaldırılsın mı?`)) return;
    try {
      await api.delete(`/settings/brand-logos/config/${encodeURIComponent(key)}`);
      await refreshLogos();
      toast.success("Özel ayar kaldırıldı");
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <div id="marka-logolari" className="scroll-mt-6 bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-1">
        <ImagePlus size={18} className="text-[#0D1B3E]" />
        <h2 className="text-base font-semibold text-slate-800">Marka logoları</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">Yeni logo ekleyin veya seçili logonun çerçeve içindeki büyüklüğünü ve konumunu ayarlayın. Değişiklikler admin ve müşteri stok ekranlarında birlikte uygulanır.</p>

      <form onSubmit={save} className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 flex flex-col items-center justify-center min-h-44">
          <div className="flex h-[104px] w-[152px] items-center justify-center" aria-label="Stok kartı logosunun iki kat büyütülmüş önizlemesi">
            <div className="origin-center scale-[2]">
              <StockBrandLogo brand={brand || "Marka"} presentation={presentation} srcOverride={previewUrl} />
            </div>
          </div>
          <span className="mt-3 text-xs font-semibold text-slate-500 text-center">2× büyütülmüş önizleme · gerçek kart oranı korunur</span>
        </div>

        <div className="space-y-4 min-w-0">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Marka</span>
              <input list="brand-logo-options" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Marka adı" className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
              <datalist id="brand-logo-options">{BRAND_LOGO_OPTIONS.map((name) => <option value={name} key={name} />)}</datalist>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Logo dosyası</span>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2.5 file:text-xs file:font-semibold file:text-slate-700 hover:file:bg-slate-200" />
              <span className="block mt-1 text-[11px] text-slate-400">JPG, PNG, WEBP veya GIF · en fazla 2 MB</span>
            </label>
          </div>

          <Range label="Yakınlaştırma" value={scale} min={0.5} max={3} step={0.05} suffix={`${Math.round(scale * 100)}%`} onChange={setScale} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Range label="Yatay konum" value={offsetX} min={-30} max={30} step={1} suffix={`${offsetX}px`} onChange={setOffsetX} />
            <Range label="Dikey konum" value={offsetY} min={-30} max={30} step={1} suffix={`${offsetY}px`} onChange={setOffsetY} />
          </div>

          <div className="flex flex-wrap gap-3 pt-1">
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#0D1B3E] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#172A58] disabled:opacity-60"><Save size={14} />{saving ? "Kaydediliyor..." : "Kaydet"}</button>
            {current && <button type="button" onClick={reset} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><RotateCcw size={14} />Varsayılana dön</button>}
          </div>
        </div>
      </form>

      {items.length > 0 && <div className="mt-6 border-t border-slate-200 pt-4"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Özel ayarı olan markalar</div><div className="flex flex-wrap gap-2">{items.map((item) => <button type="button" key={item.brand_key} onClick={() => setBrand(item.display_name)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${item.brand_key === key ? "border-[#0D1B3E] bg-[#0D1B3E] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{item.display_name}</button>)}</div></div>}
    </div>
  );
}

function Range({ label, value, min, max, step, suffix, onChange }) {
  return <label className="block"><span className="flex justify-between gap-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5"><span>{label}</span><span className="text-[#0D1B3E] tabular-nums">{suffix}</span></span><input type="range" value={value} min={min} max={max} step={step} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#0D1B3E]" /></label>;
}
